import { useEffect, useState } from 'react';
import { Download, FileText, Mail, ScanText } from 'lucide-react';
import type { ExceptionDetailRow } from '@/api/cockpit';
import { PdfPreview } from '@/components/common/PdfPreview';
import { StructuredDataView } from '@/components/common/StructuredDataView';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatRelativeTime } from '@/lib/utils';
import { buildCustomerContextItems, type OcrCustomerContext } from '../helpers';

export interface OrderSourceCardProps {
  data: ExceptionDetailRow;
  extractedCustomerContext: OcrCustomerContext | null;
}

export function OrderSourceCard(props: OrderSourceCardProps): JSX.Element {
  const { data, extractedCustomerContext } = props;
  const customerContextItems = buildCustomerContextItems(extractedCustomerContext);
  const downloadUrl = data.fileUrl ? `${data.fileUrl}?download=1` : null;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle>{data.customerName}</CardTitle>
            <CardDescription>
              PO {data.poNumber} - received {formatRelativeTime(data.receivedAt)}
            </CardDescription>
          </div>
          {downloadUrl ? (
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <a href={downloadUrl} download={data.uploadedFileName ?? undefined}>
                <Download className="h-4 w-4" />
                Download
              </a>
            </Button>
          ) : null}
        </div>
        {customerContextItems.length > 0 ? (
          <div className="grid gap-3 pt-2 sm:grid-cols-2">
            {customerContextItems.map((item) => (
              <div key={item.label} className="min-w-0 space-y-1">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {item.label}
                </div>
                <div className="whitespace-pre-wrap break-words text-sm text-foreground">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="file">
          <TabsList>
            <TabsTrigger value="source">
              <Mail className="h-4 w-4" />
              Source
            </TabsTrigger>
            <TabsTrigger value="file">
              <FileText className="h-4 w-4" />
              File
            </TabsTrigger>
            <TabsTrigger value="ocr-markdown">
              <ScanText className="h-4 w-4" />
              Extracted Text
            </TabsTrigger>
            <TabsTrigger value="ocr-metadata">
              <ScanText className="h-4 w-4" />
              Extraction Details
            </TabsTrigger>
          </TabsList>
          <TabsContent value="source" className="min-h-[360px] rounded-md border bg-muted/30 p-4">
            <SourceTabPanel data={data} />
          </TabsContent>
          <TabsContent value="file" className="min-h-[360px] rounded-md border bg-muted/30 p-4">
            <FileTabPanel data={data} />
          </TabsContent>
          <TabsContent
            value="ocr-markdown"
            className="min-h-[360px] rounded-md border bg-muted/30 p-4"
          >
            <OcrMarkdownTabPanel data={data} />
          </TabsContent>
          <TabsContent
            value="ocr-metadata"
            className="min-h-[360px] rounded-md border bg-muted/30 p-4"
          >
            <OcrMetadataTabPanel data={data} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function SourceTabPanel({ data }: { data: ExceptionDetailRow }): JSX.Element {
  return (
    <>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">Subject</div>
      <div className="font-medium">{data.emailSubject}</div>
      {data.emailSender ? (
        <>
          <div className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">From</div>
          <div className="text-sm">{data.emailSender}</div>
        </>
      ) : null}
      {data.emailReceivedAt ? (
        <>
          <div className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">Received</div>
          <div className="text-sm">{new Date(data.emailReceivedAt).toLocaleString()}</div>
        </>
      ) : null}
      <Separator className="my-3" />
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>Source information for this order.</p>
        <p>The uploaded file is available in the File tab.</p>
      </div>
    </>
  );
}

function FileTabPanel({ data }: { data: ExceptionDetailRow }): JSX.Element {
  if (data.fileUrl && data.fileExtension === 'pdf') {
    return <PdfPreview fileUrl={data.fileUrl} />;
  }
  if (
    data.fileUrl &&
    ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(data.fileExtension ?? '')
  ) {
    return (
      <div className="flex h-[360px] items-center justify-center rounded-md border bg-background p-3">
        <img
          alt={data.uploadedFileName ?? 'Uploaded file'}
          src={data.fileUrl}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }
  if (data.fileUrl && ['txt', 'csv'].includes(data.fileExtension ?? '')) {
    return <TextFilePreview fileUrl={data.fileUrl} />;
  }
  return (
    <div className="space-y-2 text-sm text-muted-foreground">
      <p>
        {data.fileUrl
          ? 'Preview is not available for this file type.'
          : 'No file preview is available for this order.'}
      </p>
      <p>
        Uploaded file:{' '}
        <span className="font-medium text-foreground">
          {data.uploadedFileName ?? 'Unknown file'}
        </span>
      </p>
    </div>
  );
}

/**
 * Inline preview for text-based source files (the email body unit is archived
 * as a .txt). The file endpoint serves it as text/plain; fetch it same-origin
 * (cookies / dev auth) and render it.
 */
function TextFilePreview({ fileUrl }: { fileUrl: string }): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setText(null);
    setFailed(false);
    fetch(fileUrl, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${String(res.status)}`);
        return res.text();
      })
      .then((body) => {
        if (active) setText(body);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [fileUrl]);

  if (failed) {
    return <div className="text-sm text-destructive">Failed to load the text preview.</div>;
  }
  if (text === null) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  return (
    <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-background p-3 text-sm leading-6 text-foreground">
      {text}
    </pre>
  );
}

function OcrMarkdownTabPanel({ data }: { data: ExceptionDetailRow }): JSX.Element {
  if (!data.ocrMarkdown) {
    return (
      <div className="text-sm text-muted-foreground">
        No extracted text is available for this order yet.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">Extracted text</div>
      <div className="text-sm text-muted-foreground">
        Processed by:{' '}
        <span className="font-medium text-foreground">{data.ocrModelVersion ?? 'n/a'}</span>
      </div>
      <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-md border bg-background p-3 text-sm leading-6 text-foreground">
        {data.ocrMarkdown}
      </pre>
    </div>
  );
}

function OcrMetadataTabPanel({ data }: { data: ExceptionDetailRow }): JSX.Element {
  if (!data.ocrMetadata) {
    return (
      <div className="text-sm text-muted-foreground">
        No extraction details are available for this order yet.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">Extraction details</div>
      <div className="text-sm text-muted-foreground">
        Processed by:{' '}
        <span className="font-medium text-foreground">{data.ocrModelVersion ?? 'n/a'}</span>
      </div>
      <StructuredDataView content={data.ocrMetadata} />
    </div>
  );
}
